import { useState } from "react";
import { X, Calendar, BookOpen, RotateCcw, CalendarPlus, User, ArrowRight } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../connector/api";

export default function RenewModal({ open, loan, onClose, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!open || !loan) return null;

  // Unify object lookups safely across different backend schemas
  const bookTitle = loan.book?.title || loan.book_title || "Untitled Document";
  const patronDisplay = loan.patron_name || `${loan.patron?.surname || ""}, ${loan.patron?.firstname || ""}`.trim() || "Assigned Patron";
  const cardNumber = loan.patron?.cardnumber || loan.cardnumber || "N/A";
  const currentDueDate = loan.due_date || "N/A";

  // Calculate dynamic visual date projection estimate text
  const projectExtendedDate = () => {
    if (!loan.due_date) return "Extended Period";
    try {
      const originalDate = new Date(loan.due_date);
      originalDate.setDate(originalDate.getDate() + 7);
      return originalDate.toISOString().split("T")[0];
    } catch {
      return "7 Days from current maturity";
    }
  };

  async function handleRenew() {
    try {
      setIsSubmitting(true);
      await api.post(`loans/${loan.id}/renew/`);
      
      toast.success("Loan timeline extended successfully");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Extension commands failed");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-stone-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-[#1a1613] rounded-2xl shadow-2xl max-w-md w-full border border-[#2e2720] overflow-hidden transform transition-all font-dynamic-title">
        
        {/* Header Block */}
        <div className="px-6 py-4 border-b border-[#2e2720] flex items-center justify-between bg-[#131110]">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl">
              <RotateCcw className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-stone-100">Renew Ledger Term</h3>
              <p className="text-xs text-stone-400">Extend item holding permissions baseline</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            disabled={isSubmitting}
            className="text-stone-400 hover:text-stone-200 hover:bg-[#201c17] p-1.5 rounded-xl transition disabled:opacity-40"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Core Workspace Context */}
        <div className="p-6 space-y-4">
          
          {/* Target Content Asset Info Block */}
          <div className="flex items-start gap-3 bg-[#131110] p-3.5 border border-[#2e2720] rounded-xl">
            <BookOpen className="h-4 w-4 text-amber-500/70 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wide">Staged Material</p>
              <h4 className="text-sm font-bold text-stone-100 truncate mt-0.5">{bookTitle}</h4>
              
              <div className="flex items-center gap-1.5 text-xs text-stone-400 mt-1">
                <User className="h-3.5 w-3.5 text-stone-500" />
                <span className="truncate text-stone-300">{patronDisplay}</span>
                <span className="font-mono text-stone-500 text-[11px]">({cardNumber})</span>
              </div>
            </div>
          </div>

          {/* Timeline Changes Comparison Component */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-stone-400 uppercase tracking-wide block">
              Maturity Adjustment Estimate
            </label>
            
            <div className="grid grid-cols-7 items-center bg-[#131110] border border-[#2e2720] rounded-xl p-3 text-center">
              
              {/* Current Date Column */}
              <div className="col-span-3 space-y-1">
                <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Current Due</p>
                <p className="text-xs font-bold font-mono text-stone-300 bg-[#1a1613] border border-[#2e2720] py-1 rounded-lg">
                  {currentDueDate}
                </p>
              </div>

              {/* Progress Flow Vector Arrow */}
              <div className="col-span-1 flex justify-center text-stone-500">
                <ArrowRight className="h-4 w-4" />
              </div>

              {/* Extended Projections Column */}
              <div className="col-span-3 space-y-1">
                <div className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400 uppercase tracking-wider">
                  <CalendarPlus className="h-3 w-3" />
                  <span>Proposed New Due</span>
                </div>
                <p className="text-xs font-bold font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 py-1 rounded-lg">
                  {projectExtendedDate()}
                </p>
              </div>

            </div>
          </div>

          {/* Descriptive Informational Disclaimer */}
          <p className="text-xs text-stone-300 leading-relaxed text-center bg-amber-500/10 border border-amber-500/20 p-3 rounded-xl">
            Executing this command updates active ledger rows, extending current holding permissions by an additional <strong className="text-amber-400 font-bold">7 days</strong>.
          </p>
        </div>

        {/* Form Commands Footer */}
        <div className="px-6 py-4 bg-[#131110] flex items-center justify-end gap-2 border-t border-[#2e2720]">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 border font-mono border-[#2e2720] bg-[#131110] text-stone-300 hover:text-stone-100 hover:bg-[#201c17] font-semibold text-xs rounded-xl transition disabled:opacity-50"
          >
            Cancel
          </button>
          
          <button
            type="button"
            disabled={isSubmitting}
            onClick={handleRenew}
            className="inline-flex items-center font-mono gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-xs rounded-xl shadow transition disabled:opacity-50"
          >
            <span>{isSubmitting ? "Extending..." : "Confirm Extension"}</span>
          </button>
        </div>

      </div>
    </div>
  );
}